/**
 * IndentDetailModal — top-level overlay that mirrors the legacy Apps Script
 * Bootstrap `#indentModal` envelope around `renderIndentDetails()`. Mounted
 * once inside ProcurementLayout; any page can fire it via
 *   useIndentModalStore.getState().open(id)
 *
 * Envelope:
 *   • z-index 1055 (Bootstrap modal z), rgba(15,23,42,0.6) backdrop.
 *   • White panel, max-width 1200, rounded 14, shadow 0 20px 50px rgba(0,0,0,0.3).
 *   • Header: dark slate (#1f2937), white text — indent id + Type pill + close X.
 *   • Body: max-height 88vh, overflow-y auto, padding 24, white.
 *   • Animations: 200ms fade-in for backdrop + scale(0.96→1) for the panel.
 *   • Backdrop click + ESC close.
 *
 * The body is the same content as the standalone route, via
 * `<IndentDetailContent inModal />` — so deep links to
 * /procurement/indents/:id still work and we have a single source of truth
 * for the Control Tower layout.
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useIndentModalStore } from './indentModalStore';
import { IndentDetailContent } from './IndentDetail';

export default function IndentDetailModal() {
  const { indentId, close } = useIndentModalStore.use();

  // ESC closes the modal — mirrors Bootstrap's `data-bs-dismiss="modal"` behaviour.
  useEffect(() => {
    if (!indentId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the modal is open (Bootstrap's modal-open class).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [indentId, close]);

  if (!indentId) return null;

  return (
    <>
      {/* Inline keyframes so we don't have to touch the global stylesheet. */}
      <style>{`
        @keyframes pc-indent-modal-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pc-indent-modal-scale {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Backdrop — click closes. */}
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          zIndex: 1054,
          animation: 'pc-indent-modal-fade 200ms ease-out',
        }}
      />

      {/* Modal panel — stopPropagation so clicks inside don't bubble to backdrop. */}
      <div
        className="p-3 sm:p-6"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1055,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="indent-modal-title"
          style={{
            pointerEvents: 'auto',
            background: '#fff',
            width: '100%',
            maxWidth: 1200,
            maxHeight: '90dvh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 14,
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            animation: 'pc-indent-modal-scale 200ms ease-out',
          }}
        >
          {/* Header — dark slate with white text, mirrors legacy modal-header. */}
          <div
            style={{
              background: '#1f2937',
              color: '#fff',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span
                id="indent-modal-title"
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  letterSpacing: 0.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {indentId}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  padding: '3px 8px',
                  borderRadius: 4,
                  background: 'rgba(255,255,255,0.12)',
                  color: '#C9CFDC',
                }}
              >
                Indent Control Tower
              </span>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                padding: 6,
                borderRadius: 6,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.85,
                transition: 'opacity 0.15s, background 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.85';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Body — scrollable, white. */}
          <div
            className="p-3 sm:p-6"
            style={{
              overflowY: 'auto',
              flex: 1,
              background: '#fff',
            }}
          >
            <IndentDetailContent key={indentId} indentId={indentId} inModal />
          </div>
        </div>
      </div>
    </>
  );
}
